import { useCallback, useMemo } from 'react'

import type { UseQueryOptions } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import type {
  Account as APIClientAccount,
  AccountSites,
  OnboardingRequirements1,
} from '@b-stock/account-api-client'

import api from '@api/account'
import useUserInfo from '@helpers/useUserInfo'
import type { RootState } from '@store/rootReducer'

export interface Account extends Omit<APIClientAccount, 'sites'> {
  sites?: {
    [key: string]: AccountSites
  }
}

export const useCurrentAccountQuery = (
  opts?: Omit<
    UseQueryOptions<unknown, unknown, Account | undefined, string[]>,
    'queryKey' | 'queryFn'
  >
) => {
  const { accountId } = useUserInfo()
  const signupId = useSelector(
    ({ auth }: RootState) => auth.signup.checkEmail._id
  )
  const id = accountId || signupId

  // Note: Query will be disabled when id is undefined, so the `UNAUTHENTICATED`
  // fallback will never actually be used
  const enabled = !!id
  const idForQuery = id || 'UNAUTHENTICATED'
  const queryKey = useMemo(
    () => ['fetchCurrentAccount', idForQuery],
    [idForQuery]
  )
  const queryFn = useCallback(
    () =>
      api.fetchAccount({
        id: idForQuery,
      }),
    [idForQuery]
  )

  return {
    // queryKey,
    ...useQuery({
      queryKey,
      queryFn,
      enabled,
      ...opts,
    }),
    // enabled: !!id, // to let the caller know if we should expect response from this query
  }
}

export const useGetSiteOnboardingRequirementsStatus = (
  opts?: Omit<
    UseQueryOptions<
      unknown,
      unknown,
      OnboardingRequirements1[] | undefined,
      string[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { accountId } = useUserInfo()
  const params: { siteAbb: string } = useParams()
  if (!params.siteAbb) {
    throw new Error('No siteAbb provided in path')
  }
  return useQuery({
    queryKey: [
      'getSiteOnboardingRequirementsStatus',
      params.siteAbb,
      accountId!,
    ],

    queryFn: () => {
      if (accountId) {
        return api.getSiteOnboardingRequirementsStatus({
          id: accountId,
          siteAbb: params.siteAbb,
        })
      }
    },

    ...opts,
    enabled: !!accountId,
  })
}
